import {
  $createNodeSelection,
  $getNodeByKey,
  $getSelection,
  $setSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
} from 'lexical'
import { mergeRegister } from '@lexical/utils'
import { useEffect, useMemo } from 'react'
import { Box, Flex, Group } from '@web/components/atoms/Layout'
import { evaluateFormula, TEXT_TYPE_TO_FORMAT } from './utils'
import { create } from 'zustand'
import { enableMapSet, produce } from 'immer'
import { MergeFieldSetting, MergeTagType } from '@web/graphql-types'
import { Popover } from '@web/components/atoms/Popover'
import { Body2 } from '@web/components/atoms/Typography'
import { Button, IconButton } from '@web/components/atoms/Button'
import { EditIcon } from '@web/components/atoms/Icons'
import { useActionDialogStore } from '@web/components/molecules/ActionDialog'
import FormulaMergeTagDialog from './FormulaMergeTagDialog'
import { Field } from '@web/components/atoms/Field'
import { Input } from '@web/components/atoms/Input'
import { PopoverContext } from '@chakra-ui/react/popover'
import { useJsProcessMergeTags } from './useProcessMergeTags'

enableMapSet()

type MergeTagProps = {
  editor: LexicalEditor
  value: Partial<MergeFieldSetting>
  fallback?: string
  options?: string
  nodeKey: string
  onFormat: (value: number) => void
  onUpdate: (value: Partial<MergeFieldSetting>) => void
}

type MergeFieldsStore = {
  isEditing: boolean
  mergeFields: Map<string, string>
  postProcessTags: Set<string>
  setMergeField: (key: string, value: string) => void
  addTagsToPostProcess: (tags: string[]) => void
}

export const useMergeFields = create<MergeFieldsStore>((set) => ({
  isEditing: false,
  mergeFields: new Map<string, string>(),
  postProcessTags: new Set<string>(),
  setMergeField: (key, value) =>
    set(
      produce((state) => {
        state.mergeFields.set(key, value)
      }),
    ),
  addTagsToPostProcess: (tags) =>
    set(
      produce((state) => {
        tags.forEach((tag) => state.postProcessTags.add(tag))
      }),
    ),
}))

const MergeTag = ({ nodeKey, editor, fallback, options, value, onFormat, onUpdate }: MergeTagProps) => {
  const isEditable = useMergeFields((state) => state.isEditing)
  const mergeFieldValue = useMergeFields(
    (state) =>
      state.mergeFields.get(value.MergeFieldSettingId || '') ||
      state.mergeFields.get(value.MergeFieldSettingId?.toLowerCase() || ''),
  )
  const tagOptions = useMemo(() => {
    if (!options) {
      return null
    }

    try {
      return JSON.parse(options)
    } catch (e) {
      console.error('Failed to parse merge tag options', e)
      return null
    }
  }, [options])

  const mergeTagFormulaValues = useMergeFields((state) => {
    if (!tagOptions?.formula) {
      return {}
    }

    return tagOptions.mergeTags.reduce((acc: Record<string, string>, tag: string) => {
      acc[tag] = state.mergeFields.get(tag) || ''
      return acc
    }, {})
  })

  useEffect(() => {
    if (!(tagOptions?.formula && tagOptions.mergeTags) || isEditable) {
      return
    }

    // send an event somewhere that we need to get values for this merge tag
    useMergeFields.getState().addTagsToPostProcess(tagOptions.mergeTags)
  }, [tagOptions])

  useEffect(() => {
    // no need to calculate if we in the editor
    if (!tagOptions?.formula || isEditable) {
      return
    }

    const result = evaluateFormula(tagOptions.formula, mergeTagFormulaValues)

    useMergeFields.getState().setMergeField(value.MergeFieldSettingId || '', result)
  }, [mergeTagFormulaValues])

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        FORMAT_ELEMENT_COMMAND,
        (payload) => {
          const node = $getNodeByKey(nodeKey)
          const selection = $getSelection()

          if (!(node?.isSelected() && selection === null)) {
            return false
          }

          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, payload)

          return false
        },
        1,
      ),
      editor.registerCommand(
        FORMAT_TEXT_COMMAND,
        (payload) => {
          const node = $getNodeByKey(nodeKey)

          if (!node?.isSelected()) {
            return false
          }

          onFormat(TEXT_TYPE_TO_FORMAT[payload])

          return false
        },
        1,
      ),
    )
  }, [])

  const isEditing = editor.isEditable()
  const isFormula = value.MergeTagType === MergeTagType.Formula

  useJsProcessMergeTags(isEditing ? [] : [value])

  return isEditable ? (
    <Popover
      hasCloseButton={false}
      lazyMount={true}
      triggerEl={
        <Box
          as="span"
          className="c-merge-tag"
          cursor="default"
          onDoubleClick={() => {
            editor.update(() => {
              const node = $getNodeByKey(nodeKey)
              if (node?.isSelected()) {
                return
              }
              const nodeSelection = $createNodeSelection()
              nodeSelection.add(nodeKey)
              $setSelection(nodeSelection)
            })
          }}
        >
          {isEditing || !mergeFieldValue ? (
            <>
              <span className="c-merge-tag__bracket">{`{`}</span>
              {value.MergeTag!.replace(/{{|}}/g, '')}
              <span className="c-merge-tag__bracket">{`}`}</span>
            </>
          ) : (
            mergeFieldValue
          )}
        </Box>
      }
    >
      {isFormula && (
        <>
          <Body2 mb="4px">Formula</Body2>
          <Group mb="4px">
            <Body2>{value.MergeTagType === MergeTagType.Formula && tagOptions?.formula}</Body2>
            <IconButton
              buttonAppearance="text-border"
              size="xs"
              onClick={() => {
                // don't really like this because FormulaMergeTagDialog is meant to be an editor only component
                // but alas we need a dialog to edit the formula and there isn't currently a good way to do that without coupling it to the editor
                useActionDialogStore.getState().actions.openDialog(
                  {
                    title: 'Custom Formula Merge Tag',
                    content: (
                      <FormulaMergeTagDialog
                        editor={editor}
                        mergeTag={{
                          name: value.MergeFieldName || '',
                          formula: tagOptions?.formula || '',
                          rounding: tagOptions?.rounding,
                          format: tagOptions?.format,
                        }}
                        onUpdate={onUpdate}
                      />
                    ),
                    ctaText: 'Update',
                    type: 'manual',
                  },
                  async () => {},
                )
              }}
            >
              <EditIcon />
            </IconButton>
          </Group>
        </>
      )}
      <Body2 mb="4px">Fallback Text</Body2>
      <Field helperText="This text will replace the merge field anytime the value is unknown." maxW="240px">
        <Input
          size="xs"
          value={fallback ?? ''}
          onChange={(e) => {
            editor.update(() => {
              onUpdate({ MergeTagFallback: e.target.value })
            })
          }}
          placeholder="Enter Fallback Text"
        />
      </Field>
      <Flex w="100%" justifyContent="end" mt="8px">
        <PopoverContext>
          {(state) => (
            <Button buttonAppearance="primary" size="xs" onClick={() => state.setOpen(false)}>
              Apply
            </Button>
          )}
        </PopoverContext>
      </Flex>
    </Popover>
  ) : (
    <span data-merge-tag={value.MergeFieldSettingId}>{mergeFieldValue || fallback}</span>
  )
}

export default MergeTag
